import { HELLO_CLOUD_ID, PUG_CLOUD_ID } from '../config';
import { type AvailableSite } from '../ui/components/SiteSwitcher/types';

/**
 * Resolve which site (`cloudId`) the logged-in experience should mount
 * against, given the user's available sites and any saved preference.
 *
 * Selection priority (first match wins):
 *   0. User's saved choice from localStorage — but only if the saved
 *      `cloudId` is still present in the available-sites list. A
 *      tenant that's been removed (deactivated, access revoked, etc.)
 *      must not pin the user to a broken state.
 *   1. Atlassian Hello site (`HELLO_CLOUD_ID`) — preferred Atlassian
 *      home tenant.
 *   2. Atlassian PUG site (`PUG_CLOUD_ID`) — secondary Atlassian home
 *      tenant.
 *   3. The first site whose `org.id` matches the user's
 *      `identity.orgId` — a sensible same-org default.
 *   4. The first site that has any `cloudId` at all — last-resort
 *      fallback so the page still renders something useful.
 *
 * Returns `undefined` only when none of the candidates match, which
 * the caller treats as "no resolved site" and leaves cloudId-dependent
 * atoms idle.
 */
export const resolveSiteSelection = ({
	sites,
	savedCloudId,
	identityOrgId,
}: {
	sites: AvailableSite[];
	savedCloudId: string | null | undefined;
	identityOrgId: string | null | undefined;
}): AvailableSite | undefined => {
	// User-saved choice from a previous session via the SiteSwitcher.
	// Only honoured when the saved cloudId is still present in the
	// available-sites list — a tenant that's been removed (deactivated,
	// access revoked, etc.) must not pin the user to a broken state.
	const userSaved = savedCloudId ? sites.find((x) => x.cloudId === savedCloudId) : undefined;

	// look for atlassian home sites
	const atlassianHello = sites.find((x) => x.cloudId === HELLO_CLOUD_ID);
	const atlassianPUG = sites.find((x) => x.cloudId === PUG_CLOUD_ID);

	// the first one we pick will the one that comes as part of identity.orgId
	const firstViaIdentity = identityOrgId
		? sites.find((x) => x.org?.id === identityOrgId)
		: undefined;
	const first = sites.find((x) => Boolean(x.cloudId));

	return userSaved ?? atlassianHello ?? atlassianPUG ?? firstViaIdentity ?? first;
};

/**
 * A site's hostname, for example `hello.atlassian.net`.
 *
 * `url` is the only field carrying the tenant's actual address; `displayName`
 * is customer-chosen prose that need not resemble it. Returns `null` for a
 * missing or unparseable `url` so a caller can leave the address out rather
 * than render a broken one.
 */
export const resolveSiteHostname = (site: AvailableSite | undefined): string | null => {
	if (!site?.url) {
		return null;
	}

	try {
		return new URL(site.url).hostname;
	} catch {
		return null;
	}
};
